import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import isNil from 'lodash-es/isNil';
import merge from 'lodash-es/merge';
import { useSelector } from 'react-redux';

import { UserPreferencesDto, UserProfileDto } from '@/api';
import { RootState } from '@/store';

export interface AuthSliceState {
  token?: string | null;
  user: UserProfileDto | null;
}

const authSlice = createSlice({
  name: 'auth',
  initialState: { token: null, user: null } as AuthSliceState,
  reducers: {
    setToken(state, { payload: token }: PayloadAction<string | null>) {
      state.token = token;
    },

    clearToken(state) {
      state.token = null;
    },

    setUser(state, { payload: user }: PayloadAction<UserProfileDto | null>) {
      state.user = user;
    },

    updateUserProfile(state, { payload: user }: PayloadAction<Partial<UserProfileDto>>) {
      state.user = merge({}, state.user, user);
    },

    updateUserPreferences(state, { payload: preferences }: PayloadAction<Partial<UserPreferencesDto>>) {
      if (state.user) {
        state.user.preferences = { ...state.user.preferences, ...preferences };
      }
    },
  },
});

const selectAuth = (state: RootState) => state.auth;

export const getAuthToken = createSelector(selectAuth, ({ token }) => token);

export const getCurrentUser = createSelector(selectAuth, ({ user }) => user);

const getIsLoggedIn = createSelector(getAuthToken, getCurrentUser, (token, user) => !isNil(token) && !isNil(user));

export const useCurrentUser = () => useSelector(getCurrentUser)!;
export const useIsLoggedIn = () => useSelector(getIsLoggedIn);
export const useLocale = () => useSelector(getCurrentUser)?.preferences.locale ?? 'en-US';

export const usePreferences = () => useSelector(getCurrentUser)!.preferences;

export const { setToken, clearToken, setUser, updateUserProfile, updateUserPreferences } = authSlice.actions;

export default authSlice;
